import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './HomePage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:7000';

const EMOTIONS = ['Happy', 'Sad', 'Angry', 'Surprised', 'Scared', 'Neutral'];

const EMOTION_EMOJI = {
  Happy: '😊', Sad: '😢', Angry: '😠', Surprised: '😮', Scared: '😨', Neutral: '😐',
};

function GalleryPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  const [gallery, setGallery] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  // Add image form state
  const [addEmotion, setAddEmotion] = useState('Happy');
  const [addImagePreview, setAddImagePreview] = useState(null);
  const [addImageData, setAddImageData] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');

  // Delete state
  const [deletingId, setDeletingId] = useState(null);

  // ── Fetch gallery ────────────────────────────────────────────────────────

  const fetchGallery = async () => {
    setIsLoading(true);
    setFetchError('');
    try {
      const res = await fetch(`${API_URL}/api/gallery`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load gallery');
      const data = await res.json();
      // Group by emotion
      const grouped = {};
      EMOTIONS.forEach(e => { grouped[e] = []; });
      (Array.isArray(data) ? data : []).forEach(item => {
        const key = item.emotion || 'Neutral';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(item);
      });
      setGallery(grouped);
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGallery();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Handle file selection ────────────────────────────────────────────────

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadError('');
    setUploadSuccess('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      setAddImagePreview(ev.target.result);
      setAddImageData(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  // ── Upload image ─────────────────────────────────────────────────────────

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!addImageData) {
      setUploadError('Please select an image file.');
      return;
    }
    setIsUploading(true);
    setUploadError('');
    setUploadSuccess('');
    try {
      const res = await fetch(`${API_URL}/api/gallery`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ emotion: addEmotion, image_data: addImageData }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      setUploadSuccess(`Image added to ${addEmotion} gallery!`);
      setAddImagePreview(null);
      setAddImageData(null);
      fetchGallery();
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  // ── Delete image ─────────────────────────────────────────────────────────

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this image from the gallery?')) return;
    setDeletingId(id);
    try {
      const res = await fetch(`${API_URL}/api/gallery/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Delete failed');
      }
      fetchGallery();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="home-shell" style={{ paddingTop: '90px' }}>

      {/* Header */}
      <header className="home-header">
        <div className="home-brand">
          <span
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer', fontSize: '20px', marginRight: '4px' }}
            title="Back to Home"
          >
            ←
          </span>
          <span className="home-logo-icon">🖼️</span>
          <span className="home-brand-name">Emotion Gallery</span>
        </div>
        <button onClick={() => navigate('/')} style={st.homeBtn}>🏠 Home</button>
      </header>

      {/* Page title */}
      <div style={{ width: '100%', maxWidth: '900px', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#1A2340', margin: '0 0 6px 0' }}>
          Manage Emotion Gallery
        </h1>
        <p style={{ fontSize: '15px', color: '#6B7280', margin: 0 }}>
          View, add, and remove emotion images used in training sessions.
        </p>
      </div>

      {/* Fetch error */}
      {fetchError && (
        <div style={st.errorBox}>
          ⚠️ {fetchError}
          <button onClick={fetchGallery} style={st.retryBtn}>Retry</button>
        </div>
      )}

      <div style={{ width: '100%', maxWidth: '900px', display: 'flex', flexDirection: 'column', gap: '32px' }}>

        {/* ── Add Image Section ── */}
        <div style={st.card}>
          <h2 style={st.cardTitle}>➕ Add Image</h2>
          <form onSubmit={handleUpload} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Emotion select */}
            <div style={st.fieldGroup}>
              <label style={st.fieldLabel}>Emotion</label>
              <select
                value={addEmotion}
                onChange={(e) => setAddEmotion(e.target.value)}
                style={st.select}
                disabled={isUploading}
              >
                {EMOTIONS.map(em => (
                  <option key={em} value={em}>{EMOTION_EMOJI[em]} {em}</option>
                ))}
              </select>
            </div>

            {/* File input */}
            <div style={st.fieldGroup}>
              <label style={st.fieldLabel}>Image File</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={isUploading}
                style={st.fileInput}
              />
            </div>

            {/* Preview */}
            {addImagePreview && (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <img
                  src={addImagePreview}
                  alt="Preview"
                  style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '12px', border: '2px solid #E5E7EB', objectFit: 'cover' }}
                />
              </div>
            )}

            {uploadError && (
              <div style={st.inlineError}>⚠️ {uploadError}</div>
            )}
            {uploadSuccess && (
              <div style={st.inlineSuccess}>✅ {uploadSuccess}</div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="submit" disabled={isUploading || !addImageData} style={{ ...st.uploadBtn, opacity: (!addImageData || isUploading) ? 0.6 : 1, cursor: (!addImageData || isUploading) ? 'not-allowed' : 'pointer' }}>
                {isUploading ? '⏳ Uploading...' : '📤 Upload Image'}
              </button>
            </div>
          </form>
        </div>

        {/* ── Gallery grouped by emotion ── */}
        {isLoading ? (
          <p style={{ color: '#6B7280', fontSize: '16px' }}>⏳ Loading gallery...</p>
        ) : (
          EMOTIONS.map(emotion => {
            const items = gallery[emotion] || [];
            return (
              <div key={emotion} style={st.card}>
                <h2 style={st.cardTitle}>
                  {EMOTION_EMOJI[emotion]} {emotion}
                  <span style={st.countBadge}>{items.length} image{items.length !== 1 ? 's' : ''}</span>
                </h2>

                {items.length === 0 ? (
                  <div style={st.emptyGroup}>
                    No images yet for <strong>{emotion}</strong>.
                  </div>
                ) : (
                  <div style={st.imageGrid}>
                    {items.map(item => (
                      <div key={item.id} style={st.imageCard}>
                        <img
                          src={item.image_data || item.image_url}
                          alt={emotion}
                          style={st.thumbnail}
                        />
                        <button
                          onClick={() => handleDelete(item.id)}
                          disabled={deletingId === item.id}
                          title="Delete image"
                          style={{
                            ...st.deleteImgBtn,
                            opacity: deletingId === item.id ? 0.5 : 1,
                            cursor: deletingId === item.id ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {deletingId === item.id ? '⏳' : '🗑️'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const st = {
  homeBtn: {
    background: '#EFF6FF', border: '1.5px solid #BFDBFE', borderRadius: '12px',
    padding: '8px 16px', fontSize: '14px', fontWeight: '700', color: '#2563EB',
    cursor: 'pointer', fontFamily: 'Segoe UI, Inter, sans-serif',
  },
  errorBox: {
    display: 'flex', alignItems: 'center', gap: '8px',
    background: '#FFF5F5', border: '2px solid #FEB2B2', borderRadius: '12px',
    padding: '12px 16px', fontSize: '14px', fontWeight: '600', color: '#C53030',
    width: '100%', maxWidth: '900px', marginBottom: '16px', boxSizing: 'border-box',
  },
  retryBtn: {
    marginLeft: 'auto', background: 'none', border: '1.5px solid #C53030',
    borderRadius: '8px', padding: '4px 12px', fontSize: '13px', fontWeight: '700',
    color: '#C53030', cursor: 'pointer',
  },
  card: {
    background: '#fff', borderRadius: '20px', padding: '28px 32px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid #E5E7EB',
  },
  cardTitle: {
    fontSize: '20px', fontWeight: '800', color: '#1A2340', margin: '0 0 20px',
    display: 'flex', alignItems: 'center', gap: '10px',
  },
  countBadge: {
    fontSize: '13px', fontWeight: '600', color: '#6B7280',
    background: '#F3F4F6', borderRadius: '99px', padding: '2px 10px',
  },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
  fieldLabel: { fontSize: '14px', fontWeight: '700', color: '#374151' },
  select: {
    padding: '10px 14px', fontSize: '15px', border: '2px solid #E5E7EB',
    borderRadius: '12px', fontFamily: 'Segoe UI, Inter, sans-serif',
    color: '#1A2340', outline: 'none', background: '#fff', cursor: 'pointer',
  },
  fileInput: {
    padding: '10px 14px', fontSize: '14px', border: '2px solid #E5E7EB',
    borderRadius: '12px', fontFamily: 'Segoe UI, Inter, sans-serif', color: '#1A2340',
    cursor: 'pointer',
  },
  inlineError: {
    background: '#FFF5F5', border: '1.5px solid #FCA5A5', borderRadius: '10px',
    padding: '8px 12px', fontSize: '13px', fontWeight: '600', color: '#991B1B',
  },
  inlineSuccess: {
    background: '#F0FDF4', border: '1.5px solid #86EFAC', borderRadius: '10px',
    padding: '8px 12px', fontSize: '13px', fontWeight: '600', color: '#166534',
  },
  uploadBtn: {
    padding: '11px 24px', background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
    border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '700',
    color: '#fff', fontFamily: 'Segoe UI, Inter, sans-serif',
    boxShadow: '0 4px 12px rgba(37,99,235,0.3)',
  },
  emptyGroup: {
    color: '#9CA3AF', fontSize: '14px', padding: '16px 0',
  },
  imageGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: '14px',
  },
  imageCard: {
    position: 'relative', borderRadius: '12px', overflow: 'hidden',
    border: '2px solid #E5E7EB', background: '#F9FAFB',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    aspectRatio: '1',
  },
  thumbnail: {
    width: '100%', height: '100%', objectFit: 'cover', display: 'block',
  },
  deleteImgBtn: {
    position: 'absolute', top: '6px', right: '6px',
    background: 'rgba(255,255,255,0.9)', border: '1.5px solid #FCA5A5',
    borderRadius: '8px', padding: '4px 7px', fontSize: '14px',
    fontFamily: 'Segoe UI, Inter, sans-serif',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
  },
};

export default GalleryPage;
